/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import {
  type Announcements,
  closestCenter,
  defaultDropAnimation,
  DndContext,
  type DragEndEvent,
  type DragMoveEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  type DropAnimation,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { type HtmlAttributes } from '../_common/types';
import { sortableTreeKeyboardCoordinates } from './tree-view-keyboard-coordinates';
import {
  type FlattenedTreeItem,
  type ItemChangedReason,
  type TreeItem,
  type TreeItemComponent,
} from './tree-view-types';
import {
  buildTree,
  flattenTree,
  getProjection,
  removeChildrenOf,
  type SensorContext,
  transformToString,
} from './tree-view-utils';
import { TreeItemWrapper } from './TreeItemWrapper';
import { SortableTreeViewItem } from './TreeViewItem';
import { TreeViewTextItem } from './TreeViewTextItem';

const indentationWidth = 20;
const ACTIVE_PLACEHOLDER_ID = 'ndl-active-tree-item';

const dropAnimationConfig: DropAnimation = {
  keyframes({ transform }) {
    return [
      {
        opacity: 1,
        transform: transformToString(transform.initial),
      },
      {
        opacity: 0,
        transform: transformToString({
          ...transform.final,
          y: transform.final.y + 5,
          x: transform.final.x + 5,
        }),
      },
    ];
  },
  easing: 'ease-out',
  sideEffects({ active }) {
    active.node.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: defaultDropAnimation.duration,
      easing: defaultDropAnimation.easing,
    });
  },
};

export interface TreeViewProps<
  T extends Record<string, unknown>,
  E extends HTMLElement,
> extends React.HTMLAttributes<HTMLOListElement> {
  /** The items to be displayed in the tree view */
  items: TreeItem<T>[];
  /** A function that is called when the items in the tree view change */
  onItemsChanged: (
    newItems: TreeItem<T>[],
    itemChangedReason: ItemChangedReason<T>,
  ) => void;
  /** The component to use for the tree items, if not provided the default component will be used */
  TreeItemComponent?: TreeItemComponent<T, E>;
  /** HTML attributes */
  htmlAttributes?: HtmlAttributes<'ol'>;
}

const TreeViewComponent = function TreeView<
  T extends Record<string, unknown>,
  E extends HTMLElement,
>({
  items,
  TreeItemComponent,
  onItemsChanged,
  htmlAttributes,
}: TreeViewProps<T, E>) {
  const flattenItems = useMemo(() => flattenTree(items), [items]);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | null>(null);
  const [focusedRowId, setFocusedRowId] = useState<UniqueIdentifier | null>(
    null,
  );

  const [currentPosition, setCurrentPosition] = useState<{
    parentId: UniqueIdentifier | null;
    overId: UniqueIdentifier;
  } | null>(null);
  const [offsetLeft, setOffsetLeft] = useState(0);

  /* ------------------------------ Setup of Data ------------------------------ */

  const flattenedAndRemovedCollapsedItems = useMemo(() => {
    const collapsedItemsIds = flattenItems
      .filter((item) => item.canHaveSubItems && item.isCollapsed)
      .map((item) => item.id);

    return removeChildrenOf(
      [...flattenItems],
      activeId !== null ? [activeId, ...collapsedItemsIds] : collapsedItemsIds,
    );
  }, [activeId, flattenItems]);

  const activeItem = useMemo(
    () => (activeId ? flattenItems.find(({ id }) => id === activeId) : null),
    [activeId, flattenItems],
  );

  const flattenedAndRemovedCollapsedItemsWithActive = useMemo(() => {
    const collapsedItems = flattenItems.reduce<UniqueIdentifier[]>(
      (acc, item) => {
        if (item.canHaveSubItems && item.isCollapsed) {
          return [...acc, item.id];
        }
        return acc;
      },
      [],
    );

    const ItemsWithCollapsedRemoved = removeChildrenOf(
      flattenItems,
      activeId ? [activeId, ...collapsedItems] : collapsedItems,
    );
    const newArray: FlattenedTreeItem<T>[] = [];
    for (const item of ItemsWithCollapsedRemoved) {
      newArray.push(item);
      if (item.id === activeId) {
        newArray.push({
          ...item,
          id: ACTIVE_PLACEHOLDER_ID,
        });
      }
    }

    return newArray;
  }, [flattenItems, activeId]);

  if (activeItem?.canHaveSubItems && !activeItem?.isCollapsed) {
    flattenedAndRemovedCollapsedItems.push({
      ...activeItem,
      id: ACTIVE_PLACEHOLDER_ID,
    });
  }

  const sensorContext: SensorContext<T> = useRef({
    items: flattenedAndRemovedCollapsedItemsWithActive,
    offset: offsetLeft,
  });

  const coordinateGetter = useMemo(() => {
    sensorContext.current.items = flattenedAndRemovedCollapsedItemsWithActive;
    sensorContext.current.offset = offsetLeft;

    return sortableTreeKeyboardCoordinates<T>(
      sensorContext,
      true,
      indentationWidth,
    );
  }, [flattenedAndRemovedCollapsedItemsWithActive, offsetLeft]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: coordinateGetter,
    }),
  );

  const projected = useMemo(
    () =>
      activeId && overId
        ? getProjection(
            flattenedAndRemovedCollapsedItemsWithActive,
            activeId,
            overId,
            offsetLeft,
            indentationWidth,
          )
        : null,
    [activeId, overId, flattenedAndRemovedCollapsedItemsWithActive, offsetLeft],
  );
  /* ------------------------------ Handlers ------------------------------ */
  function handleDragStart({ active }: DragStartEvent) {
    const activeId = active.id;
    setActiveId(activeId);
    setOverId(activeId);

    const activeItem = flattenItems.find(({ id }) => id === activeId);
    if (activeItem) {
      setCurrentPosition({
        parentId: activeItem.parentId,
        overId: activeId,
      });
    }

    document.body.style.setProperty('cursor', 'grabbing');
  }

  function handleDragMove({ delta }: DragMoveEvent) {
    setOffsetLeft(delta.x);
  }

  function handleDragOver({ over }: DragOverEvent) {
    setOverId(over?.id ?? null);
  }

  function resetState() {
    setOverId(null);
    setActiveId(null);
    setOffsetLeft(0);
    setCurrentPosition(null);

    document.body.style.setProperty('cursor', '');
  }

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      resetState();
      let overIndex = flattenItems.findIndex((item) => item.id === over?.id);

      if (over?.id === ACTIVE_PLACEHOLDER_ID) {
        overIndex = flattenItems.findIndex((item) => item.id === active.id);
      }
      if (projected && over) {
        const { depth, parentId } = projected;
        const clonedItems: FlattenedTreeItem<T>[] = [...flattenItems];

        const parentIndex = clonedItems.findIndex(
          (item) => item.id === parentId,
        );
        const activeIndex = clonedItems.findIndex(({ id }) => id === active.id);
        const activeTreeItem = clonedItems[activeIndex];

        clonedItems[activeIndex] = { ...activeTreeItem, depth, parentId };
        const parentItem = clonedItems[parentIndex];
        if (parentItem && parentItem.canHaveSubItems) {
          clonedItems[parentIndex] = { ...parentItem, isCollapsed: false };
        }
        const newItems = buildTree(
          arrayMove(clonedItems, activeIndex, overIndex),
        );

        onItemsChanged(newItems, {
          reason: 'dropped',
          item: activeTreeItem,
        });
      }
    },
    [flattenItems, onItemsChanged, projected],
  );

  function handleToggleCollapse(id: UniqueIdentifier) {
    const item = flattenItems.find((item) => item.id === id);
    if (!item || item.canHaveSubItems !== true) {
      return;
    }

    onItemsChanged(
      buildTree(
        flattenItems.map((item) => {
          if (item.id === id && item.canHaveSubItems === true) {
            return { ...item, isCollapsed: !item.isCollapsed };
          }
          return item;
        }),
      ),
      {
        reason: item.isCollapsed ? 'expanded' : 'collapsed',
        item,
      },
    );
  }

  function handleDragCancel() {
    resetState();
  }

  /* ------------------------------ Keyboard functions ------------------------------ */
  const getMovementAnnouncement = (
    eventName: string,
    activeId: UniqueIdentifier,
    overId?: UniqueIdentifier,
  ) => {
    if (overId && projected) {
      if (eventName !== 'onDragEnd') {
        if (
          currentPosition &&
          projected.parentId === currentPosition.parentId &&
          overId === currentPosition.overId
        ) {
          return;
        } else {
          setCurrentPosition({
            parentId: projected.parentId,
            overId,
          });
        }
      }

      const clonedItems: FlattenedTreeItem<T>[] = [
        ...flattenedAndRemovedCollapsedItemsWithActive,
      ];
      const overIndex = clonedItems.findIndex(({ id }) => id === overId);
      const activeIndex = clonedItems.findIndex(({ id }) => id === activeId);
      const sortedItems = arrayMove(clonedItems, activeIndex, overIndex);

      const previousItem = sortedItems[overIndex - 1];

      let announcement;
      const movedVerb = eventName === 'onDragEnd' ? 'dropped' : 'moved';
      const nestedVerb = eventName === 'onDragEnd' ? 'dropped' : 'nested';

      if (!previousItem) {
        const nextItem = sortedItems[overIndex + 1];
        announcement = `${activeId} was ${movedVerb} before ${nextItem.id}.`;
      } else {
        if (projected.depth > previousItem.depth) {
          announcement = `${activeId} was ${nestedVerb} under ${previousItem.id}.`;
        } else {
          let previousSibling: FlattenedTreeItem<T> | undefined = previousItem;
          while (previousSibling && projected.depth < previousSibling.depth) {
            const parentId: UniqueIdentifier | null = previousSibling.parentId;
            previousSibling = sortedItems.find(({ id }) => id === parentId);
          }

          if (previousSibling) {
            announcement = `${activeId} was ${movedVerb} after ${previousSibling.id}.`;
          }
        }
      }

      return announcement;
    }

    return;
  };

  const announcements: Announcements = {
    onDragStart({ active }) {
      return `Picked up ${active.id}.`;
    },
    onDragMove({ active, over }) {
      return getMovementAnnouncement('onDragMove', active.id, over?.id);
    },
    onDragOver({ active, over }) {
      return getMovementAnnouncement('onDragOver', active.id, over?.id);
    },
    onDragEnd({ active, over }) {
      return getMovementAnnouncement('onDragEnd', active.id, over?.id);
    },
    onDragCancel({ active }) {
      return `Moving was cancelled. ${active.id} was dropped in its original position.`;
    },
  };
  /* ------------------------------ Visual ------------------------------ */
  function getTrailList() {
    const trails: Record<
      UniqueIdentifier,
      ('none' | 'straight' | 'straight-curved' | 'curved')[]
    > = {};
    // Loop through the items in reverse
    [...flattenedAndRemovedCollapsedItemsWithActive]
      .reverse()
      .forEach((item, itemIndex, reversedList) => {
        const trailList: (
          | 'none'
          | 'straight'
          | 'straight-curved'
          | 'curved'
        )[] = [];
        const nextItem = reversedList[itemIndex - 1];
        // Loop through the depth of the item

        for (let trailDepth = 0; trailDepth < item.depth; trailDepth++) {
          // Not closest to the item so should be straight or none
          if (trailDepth < item.depth - 1) {
            if (nextItem === undefined) {
              trailList.push('none');
              continue;
            } else if (trails[nextItem?.id][trailDepth] === 'none') {
              trailList.push('none');
              continue;
            } else if (nextItem && nextItem.depth <= trailDepth) {
              trailList.push('none');
              continue;
            }
            trailList.push('straight');
          } else {
            if (
              (nextItem && trails[nextItem?.id][trailDepth] === 'none') ||
              nextItem === undefined ||
              nextItem.depth <= trailDepth
            ) {
              trailList.push('curved');
            } else {
              trailList.push('straight-curved');
            }
          }
        }
        trails[item.id] = trailList;
      });
    return trails;
  }

  const measuring = {
    droppable: {
      strategy: MeasuringStrategy.Always,
    },
  };

  const trails = getTrailList();
  const rowIdWithFocus =
    focusedRowId ?? flattenedAndRemovedCollapsedItemsWithActive[0]?.id;
  return (
    <DndContext
      accessibility={{ announcements }}
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragOver={handleDragOver}
      onDragCancel={handleDragCancel}
      measuring={measuring}
    >
      <SortableContext
        items={flattenedAndRemovedCollapsedItemsWithActive.map(
          (item) => item.id,
        )}
        strategy={verticalListSortingStrategy}
      >
        <ol className="ndl-tree-view-list" role="tree" {...htmlAttributes}>
          {flattenedAndRemovedCollapsedItemsWithActive.map((item) => (
            <SortableTreeViewItem<T, E>
              key={item.id}
              depth={
                item.id === activeId && projected ? projected.depth : item.depth
              }
              indentationWidth={indentationWidth}
              item={item}
              isLast={false}
              parent={flattenItems.find((i) => i.id === item.parentId) ?? null}
              id={item.id}
              trails={trails[item.id]}
              keepGhostInPlace={item.id === ACTIVE_PLACEHOLDER_ID}
              TreeItemComponent={
                TreeItemComponent === undefined
                  ? (TreeViewTextItem as TreeItemComponent<T, E>)
                  : TreeItemComponent
              }
              onCollapse={() => handleToggleCollapse(item.id)}
              onItemsChanged={(newItems, itemChangedReason) =>
                onItemsChanged(buildTree(newItems), itemChangedReason)
              }
              items={flattenItems}
              tabIndex={item.id === rowIdWithFocus ? 0 : -1}
              onFocus={() => setFocusedRowId(item.id)}
              shouldDisableSorting={item.isSortable === false}
            />
          ))}
        </ol>
        {/* Portal is used to show the item the user drags */}
        {createPortal(
          <DragOverlay dropAnimation={dropAnimationConfig}></DragOverlay>,
          document.body,
        )}
      </SortableContext>
    </DndContext>
  );
};

const TreeViewComponents = {
  SortableTreeViewItem,
  TreeItemWrapper,
  TreeViewTextItem,
};

export const TreeView = Object.assign(TreeViewComponent, TreeViewComponents);
